import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, photoUrl } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { ArrowLeft, Heart, Lock, MapPin, Briefcase, Star, Shield, MoreVertical, Flag, Ban, EyeOff, Camera, Moon, Compass, Sparkles } from "lucide-react";
import PhotoLightbox from "@/components/PhotoLightbox";
import TrustBadges from "@/components/TrustBadges";

const DEEN_LABEL = {
  born_practicing: "Født muslim — praktiserende hele livet",
  born_returning:  "Født muslim — genfundet praksis",
  revert:          "Revert (konvertit)",
  growing:         "Praktiserer gradvist",
  seeking:         "Søgende / lærende",
};

const FACTOR_LABELS = {
  intention: "Ægteskabsintention", religion: "Tro & praksis", location: "Bopæl",
  family: "Familie & børn", lifestyle: "Livsstil", age: "Alder",
  languages: "Sprog", education: "Uddannelse",
};

const TIMELINE_LABEL = { ready: "Klar nu", "6m": "Inden 6 måneder", "1y": "Inden 1 år", "1_2y": "1-2 år", exploring: "Udforsker seriøst" };

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [reportReason, setReportReason] = useState("harassment");
  const [reportDetails, setReportDetails] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [prompts, setPrompts] = useState([]);

  const load = async () => {
    try {
      const [{ data }, promptsRes] = await Promise.all([
        api.get(`/profile/${id}`),
        api.get("/profile/prompts").catch(() => ({ data: [] })),
      ]);
      setP(data); setPrompts(promptsRes.data || []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const express = async () => {
    setBusy(true);
    try { await api.post("/matches/interests", { to_profile_id: id }); toast.success("Interesse sendt."); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const requestPhotoAccess = async () => {
    try { await api.post("/photos/access-request", { profile_id: id }); toast.success("Anmodning sendt."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const passUser = async () => {
    if (!p?.user_id || !confirm("Skjul denne profil fra din feed?")) return;
    try { await api.post("/matches/pass", { user_id: p.user_id }); navigate("/matches"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const blockUser = async () => {
    if (!p?.user_id || !confirm("Bloker denne bruger?")) return;
    try { await api.post("/safety/block", { user_id: p.user_id }); toast.success("Bruger blokeret."); navigate("/matches"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitReport = async () => {
    try {
      await api.post("/safety/report", { user_id: p.user_id, reason: reportReason, details: reportDetails });
      setShowReport(false); setReportDetails(""); toast.success("Tak — rapport modtaget.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) return <div className="max-w-3xl mx-auto p-8 text-destructive text-sm">{err}</div>;
  if (!p) return <div className="max-w-3xl mx-auto p-8 text-brown-500">Indlæser…</div>;

  const limited = p.limited;
  const photos = p.photos || [];
  const heroPhoto = photos[0];
  const captions = p.photo_captions || {};
  const promptLabelFor = (key) => prompts.find((x) => x.key === key)?.label || key;
  const answered = (p.prompts || []).filter((x) => x.answer);

  return (
    <div className="min-h-screen bg-sand-50 pb-32" data-testid="profile-detail-page">
      <div className="fixed top-0 inset-x-0 z-30 bg-white/80 backdrop-blur border-b border-sand-300">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="btn-ghost text-sm" data-testid="detail-back">
            <ArrowLeft className="h-4 w-4" /> Tilbage
          </button>
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="btn-ghost p-2" data-testid="detail-menu-btn" aria-label="Menu">
              <MoreVertical className="h-5 w-5" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-sand-300 rounded-xl shadow-soft py-1 z-40" data-testid="detail-menu">
                <MenuItem onClick={() => { setShowMenu(false); passUser(); }} testid="menu-pass"><EyeOff className="h-4 w-4" /> Skjul fra feed</MenuItem>
                <MenuItem onClick={() => { setShowMenu(false); requestPhotoAccess(); }} testid="menu-photo-request"><Camera className="h-4 w-4" /> Bed om private fotos</MenuItem>
                <MenuItem onClick={() => { setShowMenu(false); setShowReport(true); }} testid="menu-report"><Flag className="h-4 w-4" /> Rapportér</MenuItem>
                <MenuItem onClick={() => { setShowMenu(false); blockUser(); }} testid="menu-block" danger><Ban className="h-4 w-4" /> Blokér</MenuItem>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* HERO SPLASH */}
      <section className="relative w-full h-[62vh] min-h-[420px] max-h-[720px] bg-olive-900" data-testid="hero-splash">
        {limited ? (
          heroPhoto ? (
            <img src={photoUrl(heroPhoto)} alt="" loading="eager" className="absolute inset-0 w-full h-full object-cover blur-photo" data-testid="hero-photo-blurred" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-sand-200 to-olive-300 blur-photo" />
          )
        ) : heroPhoto ? (
          <button className="absolute inset-0 w-full h-full focus:outline-none group" onClick={() => setLightbox(0)} aria-label="Åbn fotogalleri" data-testid="hero-photo">
            <img src={photoUrl(heroPhoto)} alt="" loading="eager" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          </button>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">Intet foto uploadet</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-olive-900/95 via-olive-900/40 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 pt-24 max-w-4xl mx-auto text-white pointer-events-none">
          <div className="pointer-events-auto"><TrustBadges profile={p} /></div>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl flex items-baseline gap-2 flex-wrap drop-shadow-md">
            <span data-testid="detail-name">{p.display_name}</span>
            {p.age && <span className="text-white/90 text-3xl sm:text-4xl">, {p.age}</span>}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-white/95 text-sm flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-gold-300" /> {p.city}{p.country ? `, ${p.country}` : ""}</span>
            {p.profession && <span className="flex items-center gap-1"><Briefcase className="h-4 w-4 text-gold-300" /> {p.profession}</span>}
            {p.compatibility_score > 0 && (
              <span className="flex items-center gap-1 bg-white/15 backdrop-blur px-2 py-0.5 rounded-full"><Star className="h-3.5 w-3.5 text-gold-300" /> {p.compatibility_score}% match</span>
            )}
          </div>
        </div>
        {limited && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <Link to="/pricing" className="bg-white/95 rounded-full px-4 py-2 text-sm text-olive-700 border border-sand-300 flex items-center gap-2" data-testid="detail-upgrade-link">
                <Lock className="h-4 w-4" /> Tegn medlemskab for at se fotos
              </Link>
            </div>
          </div>
        )}
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {p.compatibility_breakdown && Object.keys(p.compatibility_breakdown).length > 0 && (
          <div className="card-soft p-5" data-testid="compat-highlights">
            <div className="eyebrow flex items-center gap-2 text-brown-500"><Sparkles className="h-3 w-3" /> Hvorfor I matcher</div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {Object.entries(p.compatibility_breakdown).sort((a, b) => b[1].score - a[1].score).slice(0, 6).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-sm">
                  <span className="text-brown-600 flex-1">{FACTOR_LABELS[k] || k}</span>
                  <div className="w-24 h-1.5 bg-sand-200 rounded-full overflow-hidden">
                    <div className={`h-full ${v.score >= 70 ? "bg-olive-500" : v.score >= 40 ? "bg-gold-500" : "bg-brown-400"}`} style={{ width: `${v.score}%` }} />
                  </div>
                  <span className="w-10 text-right text-brown-500 text-xs">{v.score}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!limited && photos.length > 1 && (
          <div>
            <div className="eyebrow mb-3">Fotos</div>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {photos.slice(1).map((src, i) => (
                <button key={i} onClick={() => setLightbox(i + 1)} className="aspect-square rounded-xl overflow-hidden border border-sand-300 focus:outline-none focus:ring-2 focus:ring-olive-500 group relative" data-testid={`photo-thumb-${i + 1}`} aria-label={`Åbn foto ${i + 2}`}>
                  <img src={photoUrl(src)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  {captions[String(i + 1)] && <div className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/50 backdrop-blur rounded px-1.5 py-0.5 truncate">{captions[String(i + 1)]}</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {!limited && answered.length > 0 && (
          <div className="space-y-3" data-testid="prompt-cards">
            {answered.map((a, i) => (
              <div key={i} className="card-soft p-5 relative overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="absolute -top-2 -left-2 text-6xl font-serif text-gold-500/10 select-none">"</div>
                <div className="text-xs uppercase tracking-widest text-gold-700 relative">{promptLabelFor(a.key)}</div>
                <p className="mt-2 font-serif text-xl text-olive-700 leading-snug relative">{a.answer}</p>
              </div>
            ))}
          </div>
        )}

        {!limited && (p.deen_journey_type || p.deen_journey_note) && (
          <div className="card-soft p-6 bg-gradient-to-br from-olive-50 to-sand-50 border-olive-500/20" data-testid="deen-journey">
            <div className="flex items-center gap-2"><Moon className="h-5 w-5 text-olive-600" /><div className="eyebrow">Deen-rejse</div></div>
            {p.deen_journey_type && <div className="mt-2 font-serif text-lg text-olive-700">{DEEN_LABEL[p.deen_journey_type] || p.deen_journey_type}</div>}
            {p.deen_journey_note && <p className="mt-2 text-brown-600 whitespace-pre-line text-sm">{p.deen_journey_note}</p>}
          </div>
        )}

        {!limited && (p.life_now || p.life_next || p.marriage_timeline) && (
          <div data-testid="life-timeline">
            <div className="eyebrow mb-3 flex items-center gap-2"><Compass className="h-3 w-3" /> Livs-tidslinje</div>
            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-olive-500/30" />
              {p.life_now && <TimelinePoint label="Lige nu" text={p.life_now} tone="filled" />}
              {p.life_next && <TimelinePoint label="Om 12 måneder" text={p.life_next} />}
              {p.marriage_timeline && <TimelinePoint label="Ægteskabsvindue" text={TIMELINE_LABEL[p.marriage_timeline] || p.marriage_timeline} tone="gold" />}
            </div>
          </div>
        )}

        {limited ? (
          <div className="card-soft p-6 bg-gold-100/60 border-gold-500/30">
            <div className="flex items-center gap-2 text-gold-700"><Shield className="h-4 w-4" /> <span className="font-medium">Privat profil</span></div>
            <p className="mt-2 text-sm text-brown-600">For at se den fulde biodata, familiebaggrund og starte en samtale, aktivér et medlemskab.</p>
            <div className="mt-4"><Link to="/pricing" className="btn-primary" data-testid="detail-cta-pricing">Se planer</Link></div>
            {p.about_me_preview && <div className="mt-6 pt-4 border-t border-sand-300 text-sm text-brown-600 italic">„{p.about_me_preview}…"</div>}
          </div>
        ) : (
          <>
            {p.about_me && <Section title="Om mig"><p className="text-brown-600 whitespace-pre-line leading-relaxed">{p.about_me}</p></Section>}
            {p.family_background && <Section title="Familie"><p className="text-brown-600 whitespace-pre-line leading-relaxed">{p.family_background}</p></Section>}
            <div className="flex flex-wrap gap-2">
              {p.sect && <Chip>{p.sect}</Chip>}
              {p.religious_practice && <Chip>{p.religious_practice.replaceAll("_", " ")}</Chip>}
              {(p.values || []).map((v) => <Chip key={v}>{v}</Chip>)}
            </div>
          </>
        )}
      </div>

      {!limited && (
        <div className="fixed bottom-16 sm:bottom-4 inset-x-0 z-20 px-4 pointer-events-none">
          <div className="max-w-md mx-auto pointer-events-auto">
            <button onClick={express} disabled={busy} className="w-full btn-primary text-base py-3 shadow-soft disabled:opacity-60" data-testid="detail-express-interest">
              <Heart className="h-5 w-5 fill-rose-500 text-rose-500" /> {busy ? "Sender…" : "Vis interesse"}
            </button>
          </div>
        </div>
      )}

      {lightbox !== null && photos.length > 0 && (
        <PhotoLightbox photos={photos} captions={captions} startIndex={lightbox} onClose={() => setLightbox(null)} />
      )}

      {showReport && (
        <div className="fixed inset-0 z-50 bg-olive-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="report-modal">
          <div className="bg-white rounded-2xl shadow-soft max-w-lg w-full p-6 sm:p-8 animate-fade-up">
            <h3 className="font-serif text-2xl text-olive-700">Rapportér profil</h3>
            <p className="mt-2 text-sm text-brown-600">Beskyt fællesskabet. Vi behandler alle rapporter fortroligt.</p>
            <label className="block mt-4">
              <span className="text-sm text-brown-600">Årsag</span>
              <select className="field mt-1 w-full" value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-reason">
                <option value="fake_profile">Falsk profil</option>
                <option value="harassment">Chikane / upassende adfærd</option>
                <option value="inappropriate">Upassende indhold</option>
                <option value="scam">Svindel / bedrageri</option>
                <option value="married">Allerede gift / vildledende status</option>
                <option value="spam">Spam</option>
                <option value="underage">Bekymring om mindreårig</option>
                <option value="other">Andet</option>
              </select>
            </label>
            <label className="block mt-3">
              <span className="text-sm text-brown-600">Detaljer (valgfrit)</span>
              <textarea rows={3} maxLength={1000} value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} className="field mt-1 w-full" placeholder="Beskriv kort hvad du oplevede…" data-testid="report-details" />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowReport(false)} className="btn-secondary text-sm px-4 py-2" data-testid="report-cancel">Annullér</button>
              <button onClick={submitReport} className="btn-primary text-sm px-5 py-2" data-testid="report-submit">Send rapport</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return <div><div className="eyebrow mb-2">{title}</div>{children}</div>;
}
function Chip({ children }) {
  return <span className="text-xs px-3 py-1 rounded-full bg-sand-100 text-brown-700 border border-sand-300 capitalize">{children}</span>;
}
function TimelinePoint({ label, text, tone = "" }) {
  const dotClass = tone === "gold" ? "bg-gold-500" : tone === "filled" ? "bg-olive-500" : "bg-white border-2 border-olive-500";
  return (
    <div className="relative py-2">
      <span className={`absolute -left-[22px] top-3 h-3 w-3 rounded-full ${dotClass}`} />
      <div className="text-[10px] uppercase tracking-widest text-brown-500">{label}</div>
      <div className="text-sm text-olive-700 font-medium">{text}</div>
    </div>
  );
}
function MenuItem({ children, onClick, testid, danger }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-sand-100 ${danger ? "text-destructive" : "text-brown-700"}`} data-testid={testid}>
      {children}
    </button>
  );
}
